import { Pipe, PipeTransform } from '@angular/core';

/** Milliseconds in one day. */
const MS_PER_DAY = 86_400_000;
/** Milliseconds in one hour. */
const MS_PER_HOUR = 3_600_000;
/** Days beyond which the message switches from relative to an absolute date. */
const MAX_DAYS_RELATIVE = 30;

/**
 * Formats how long ago a dashboard item occurred, prefixed with
 * caller-supplied text (e.g. `"Requested"`, `"Last edited"`).
 *
 * Output tiers (largest applicable unit wins):
 * - `< 1` hour   → `"{preText} just now"`
 * - `< 24` hours → `"{preText} X hour(s) ago"`
 * - `<= 30` days → `"{preText} X day(s) ago"`
 * - `> 30` days  → `"{preText} DD-Mon-YYYY"`
 *
 * Future or unparseable dates fall back to `"{preText} recently"`.
 *
 * @example
 * {{ item.requested_date | wcgRequestedMessage:'Requested' }}
 * <!-- "Requested 3 hours ago" -->
 */
@Pipe({
  name: 'wcgRequestedMessage',
})
export class RequestedMessagePipe implements PipeTransform {
  /**
   * Transforms an ISO date string into a relative "{preText} … ago" message.
   *
   * @param value - ISO datetime string (e.g. `2026-07-28T09:00:00.000000`)
   *   or date-only string (e.g. `2026-07-28`), interpreted in local time.
   * @param preText - Text to prefix the message with, e.g. `"Requested"` or
   *   `"Last edited"`.
   * @returns Relative message such as `"Requested 2 days ago"` or
   *   `"Last edited just now"`; `"{preText} recently"` for future or invalid
   *   dates.
   */
  transform(value: string | null, preText: string): string {
    // Date-only strings anchor to local midnight so a UTC parse can't shift the day
    if (!value) {
      return '';
    }
    const date = value.includes('T')
      ? new Date(value)
      : new Date(`${value}T00:00:00`);

    const diffMs = Date.now() - date.getTime();
    if (Number.isNaN(date.getTime()) || diffMs < 0) {
      return `${preText} recently`;
    }

    const hours = Math.floor(diffMs / MS_PER_HOUR);
    if (hours < 1) return `${preText} just now`;
    if (hours < 24) {
      return `${preText} ${hours} hour${hours === 1 ? '' : 's'} ago`;
    }

    const days = Math.floor(diffMs / MS_PER_DAY);
    if (days <= MAX_DAYS_RELATIVE) {
      return `${preText} ${days} day${days === 1 ? '' : 's'} ago`;
    }

    return `${preText} ${this._formatDate(date)}`;
  }

  /**
   * Formats a date as `DD-Mon-YYYY` for items older than
   * {@link MAX_DAYS_RELATIVE} days.
   *
   * @param date - The date to format.
   * @returns Date formatted like `"28-Jul-2026"`.
   */
  private _formatDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = date
      .toLocaleString('en-US', { month: 'short' })
      .toUpperCase();
    return `${day}-${month.charAt(0)}${month.slice(1).toLowerCase()}-${date.getFullYear()}`;
  }
}
